import {create} from 'zustand';
import {Marketer} from '../models/Marketer';
import {CreateMarketerRequest} from '../models/CreateMarketer';
import {UpdateMarketerProfileRequest, UpdateMarketerPasswordRequest} from '../models/UpdateMarketer';
import {Pagination} from '../../../Data/types/BaseResponse';
import {MarketersFactory} from './marketersFactory';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {MarketerProfileUpdate, MarketerPasswordUpdate} from '../../../types/MarketerTypes';

interface MarketersState {
  // Data
  marketers: Marketer[];
  pagination: Pagination | undefined;

  // Current free-text search term (server-side; spans all pages)
  searchTerm: string;

  // Loading states
  loading: boolean;

  // Error states
  error: string | null;

  // Actions
  fetchMarketers: (page?: number, limit?: number, search?: string) => Promise<void>;
  fetchAllMarketers: () => Promise<void>;
  createMarketer: (data: CreateMarketerRequest) => Promise<Marketer>;
  updateMarketerProfile: (id: number, data: UpdateMarketerProfileRequest) => Promise<MarketerProfileUpdate>;
  updateMarketerPassword: (id: number, data: UpdateMarketerPasswordRequest) => Promise<MarketerPasswordUpdate>;
  deleteMarketer: (id: number) => Promise<boolean>;
  setPagination: (pagination: Pagination | undefined) => void;
  refreshData: () => Promise<void>;
  clearErrors: () => void;
}

export const useMarketersStore = create<MarketersState>((set, get) => {
  // Initialize use cases using factory
  const getAllMarketers = MarketersFactory.createGetAllMarketersUseCase();
  const getAllMarketersWithoutPagination = MarketersFactory.createGetAllMarketersWithoutPaginationUseCase();
  const createMarketerUseCase = MarketersFactory.createCreateMarketerUseCase();
  const updateMarketerProfileUseCase = MarketersFactory.createUpdateMarketerProfileUseCase();
  const updateMarketerPasswordUseCase = MarketersFactory.createUpdateMarketerPasswordUseCase();
  const deleteMarketerUseCase = MarketersFactory.createDeleteMarketerUseCase();
  const errorHandlingService = StandardizedErrorHandler.getInstance();

  return {
    // Initial state
    marketers: [],
    pagination: undefined,
    searchTerm: '',
    loading: false,
    error: null,

    // Actions
    fetchMarketers: async (page = PAGINATION_CONFIG.DEFAULT_PAGE, limit = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE, search) => {
      // When `search` is omitted (e.g. pagination/refresh) keep the active term
      // so paging through filtered results doesn't reset the search.
      const term = search !== undefined ? search : get().searchTerm;
      set({loading: true, error: null, searchTerm: term});
      try {
        const {items, pagination} = await getAllMarketers.execute(page, limit, term);

        set({marketers: items || [], pagination, loading: false});
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({error: appError.message, loading: false});
        errorHandlingService.handleError(err); // For notifications/logging
      }
    },

    // Loads the COMPLETE marketer list (limit=all) for selectors/dropdowns that
    // must show every marketer, not a single page. Keep `fetchMarketers` for the
    // paginated management table.
    fetchAllMarketers: async () => {
      set({loading: true, error: null});
      try {
        const {items, pagination} = await getAllMarketersWithoutPagination.execute();

        set({marketers: items || [], pagination, loading: false});
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({error: appError.message, loading: false});
        errorHandlingService.handleError(err); // For notifications/logging
      }
    },

    createMarketer: async (data: CreateMarketerRequest) => {
      try {
        const response = await createMarketerUseCase.execute(data);
        // Refresh data after creation
        try {
          await get().refreshData();
        } catch (refreshError) {
          // Silently ignore refresh errors to prevent duplicate notifications
          // The main operation already succeeded, refresh failure is non-critical
        }
        return response;
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    updateMarketerProfile: async (id: number, data: UpdateMarketerProfileRequest) => {
      try {
        const response = await updateMarketerProfileUseCase.execute(id, data);
        // Refresh data after update
        try {
          await get().refreshData();
        } catch (refreshError) {
          // Silently ignore refresh errors to prevent duplicate notifications
          // The main operation already succeeded, refresh failure is non-critical
        }
        return response;
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    updateMarketerPassword: async (id: number, data: UpdateMarketerPasswordRequest) => {
      try {
        const response = await updateMarketerPasswordUseCase.execute(id, data);
        return response;
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    deleteMarketer: async (id: number) => {
      try {
        const success = await deleteMarketerUseCase.execute(id);
        if (success) {
          // Optimistically remove the deleted marketer from the state
          const currentMarketers = get().marketers;
          const updatedMarketers = currentMarketers.filter(m => m.id !== id);
          set({marketers: updatedMarketers});

          // Then refresh data from server to ensure consistency
          await get().refreshData();
        }
        return success;
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        // If error occurs, refresh to restore correct state (but don't handle refresh errors)
        try {
          await get().refreshData();
        } catch (refreshError) {
          // Silently ignore refresh errors to prevent duplicate notifications
          // The delete operation error is already being thrown, refresh is just for cleanup
        }
        throw err;
      }
    },

    setPagination: (pagination) => {
      set({pagination});
    },

    refreshData: async () => {
      const currentState = get();
      const page = currentState.pagination?.page || PAGINATION_CONFIG.DEFAULT_PAGE;
      const limit = currentState.pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE;
      try {
        await get().fetchMarketers(page, limit);
      } catch (err) {
        // Silently ignore refresh errors to prevent duplicate notifications
        // Errors are already handled at component level when fetchMarketers is called directly
      }
    },

    clearErrors: () => {
      set({error: null});
    }
  };
});
